"use client";
import React from "react";
import Image from "next/image";
import Link from "next/link";
import { formatDistanceToNow } from "date-fns";
import { Calendar, Clock, ArrowRight, User } from "lucide-react";
import { BlogItem } from "@/data/blogData";

interface HomeBlogCardProps {
  item: BlogItem;
}

export default function HomeBlogCard({ item }: HomeBlogCardProps) {
  // Format date safely
  const formattedDate = (() => {
    try {
      if (item.createdAt) {
        return formatDistanceToNow(new Date(item.createdAt), {
          addSuffix: true,
        });
      }
    } catch (e) {
      // fallback if date parsing fails
    }
    return "Recently published";
  })();

  const targetLink = item.slug ? `/blogs/${item.slug}` : `/blogs/${item.blogId || item._id}`;

  return (
    <article className="group bg-white rounded-md overflow-hidden border border-zinc-200/80 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between h-full">
      {/* Image Container */}
      <div className="relative h-60 w-full overflow-hidden bg-zinc-100">
        <Image
          src={typeof item.banner === "string" ? item.banner : "/blogs.jpg"}
          alt={item.title || "Blog Image"}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          className="object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />
      </div>

      {/* Content Body */}
      <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
        <div className="space-y-3">
          {/* Metadata Row */}
          <div className="flex items-center justify-between text-xs text-zinc-500 font-medium">
            <span className="flex items-center gap-1.5 text-[#E25C23]">
              <Calendar className="w-3.5 h-3.5" />
              {formattedDate}
            </span>
            <span className="flex items-center gap-1.5 text-zinc-600">
              <User className="w-3.5 h-3.5" />
              {item.authorName || "Prem Gurung"}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-zinc-900 group-hover:text-[#E25C23] transition-colors duration-200 line-clamp-2 leading-snug">
            <Link href={targetLink} className="hover:no-underline">
              {item.title}
            </Link>
          </h3>

          {/* Description Snippet */}
          <div
            className="text-zinc-600 text-sm leading-relaxed line-clamp-3 font-normal"
            dangerouslySetInnerHTML={{
              __html: item.description ? item.description.trim() : "",
            }}
          />
        </div>

        {/* Card Footer / Action Link */}
        <div className="pt-4 border-t border-zinc-100 flex items-center justify-between">
          {item.readTime && (
            <span className="text-xs text-zinc-400 font-medium flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {item.readTime}
            </span>
          )}
          <Link
            href={targetLink}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#E25C23] hover:text-black transition-colors duration-200 ml-auto group/btn"
          >
            <span>Read Story</span>
            <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-200" />
          </Link>
        </div>
      </div>
    </article>
  );
}
